// Detect common typos in email domains and suggest a correction.
// e.g. "name@gmial.com" → suggest "name@gmail.com".
//
// Returns either:
//   null                                — no suspected typo
//   { suggested: 'name@gmail.com' }     — likely typo, suggest correction
//
// Used by:
//   - PersonalAndContactStep (real-time warning under the email field, click-to-fix)
//   - ReviewStep (warning in the Personal Details section, click jumps to Contact)

// Common, high-volume UK consumer email providers + their frequent typos.
// Keep the list focused — we only want VERY confident corrections to avoid
// false positives (e.g. small businesses with legitimate custom domains).
const TYPO_MAP = {
  // Gmail
  'gmial.com': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmailcom': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gimail.com': 'gmail.com',
  'gmali.com': 'gmail.com',
  'gmaol.com': 'gmail.com',
  'ggmail.com': 'gmail.com',
  // Hotmail
  'hotnail.com': 'hotmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmai.com': 'hotmail.com',
  'hotmaill.com': 'hotmail.com',
  'hotmal.com': 'hotmail.com',
  'hotmail.co': 'hotmail.com',
  'hotmail.cm': 'hotmail.com',
  'hotmail.con': 'hotmail.com',
  'homtail.com': 'hotmail.com',
  'hotmial.co.uk': 'hotmail.co.uk',
  'hotmial.couk': 'hotmail.co.uk',
  'hotmal.co.uk': 'hotmail.co.uk',
  'hotmail.couk': 'hotmail.co.uk',
  // Yahoo
  'yhoo.com': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'yaho.com': 'yahoo.com',
  'yahho.com': 'yahoo.com',
  'yahoo.co': 'yahoo.com',
  'yahoo.cm': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  'yahoo.couk': 'yahoo.co.uk',
  'yhoo.co.uk': 'yahoo.co.uk',
  // Outlook / Live / MSN
  'outlok.com': 'outlook.com',
  'outloook.com': 'outlook.com',
  'oulook.com': 'outlook.com',
  'outlook.co': 'outlook.com',
  'outlook.cm': 'outlook.com',
  'outlook.con': 'outlook.com',
  'live.co': 'live.com',
  'live.cm': 'live.com',
  // iCloud / Apple
  'icoud.com': 'icloud.com',
  'iclod.com': 'icloud.com',
  'iclould.com': 'icloud.com',
  'icoud.co.uk': 'icloud.com',
  // BT / Sky / TalkTalk (UK consumer)
  'btinternet.co': 'btinternet.com',
  'btinternt.com': 'btinternet.com',
  'btinternet.con': 'btinternet.com',
  'sky.co': 'sky.com',
  'talktalk.co': 'talktalk.net',
  'talktal.net': 'talktalk.net',
  // AOL
  'aol.co': 'aol.com',
  'aol.cm': 'aol.com',
  'ail.com': 'aol.com',
  // Generic .com / .co.uk slips
};

export const detectEmailTypo = (email) => {
  if (typeof email !== 'string' || !email.includes('@')) return null;
  const at = email.lastIndexOf('@');
  const local = email.slice(0, at);
  const domain = email.slice(at + 1).trim().toLowerCase();
  if (!domain || !local) return null;

  // Exact-match typo lookup.
  const correction = TYPO_MAP[domain];
  if (correction && correction !== domain) {
    return { suggested: `${local}@${correction}` };
  }

  return null;
};

export default detectEmailTypo;
